import {
  collection,
  query,
  where,
  getCountFromServer,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import { firestore } from "@/helpers/firebase/firebase";

/**
 * Get the count of quotes by a specific member
 */
export async function getMemberQuoteCount(
  groupId: string,
  memberUuid: string,
): Promise<number> {
  const quotesCollection = collection(firestore, `groups/${groupId}/quotes`);
  const q = query(
    quotesCollection,
    where("authors", "array-contains", memberUuid),
  );
  const result = await getCountFromServer(q);
  return result.data().count;
}

/**
 * Get the count of photos created by a specific member
 */
export async function getMemberPhotoCount(
  groupId: string,
  memberUuid: string,
): Promise<number> {
  const photosCollection = collection(firestore, `groups/${groupId}/photos`);
  const q = query(photosCollection, where("created_by", "==", memberUuid));
  const result = await getCountFromServer(q);
  return result.data().count;
}

/**
 * Get the count of events a member is marked as available for
 */
export async function getMemberEventCount(
  groupId: string,
  memberUuid: string,
): Promise<number> {
  const eventsCollection = collection(firestore, `groups/${groupId}/events`);
  const q = query(
    eventsCollection,
    where("available", "array-contains", memberUuid),
  );
  const result = await getCountFromServer(q);
  return result.data().count;
}

/**
 * Get the latest quote by a specific member
 */
export async function getMemberLatestQuote(
  groupId: string,
  memberUuid: string,
): Promise<string | null> {
  const quotesCollection = collection(firestore, `groups/${groupId}/quotes`);
  const q = query(
    quotesCollection,
    where("authors", "array-contains", memberUuid),
    orderBy("timestamp", "desc"),
    limit(1),
  );
  const result = await getDocs(q);

  if (result.empty) {
    return null;
  }

  const quoteData = result.docs[0]?.data() as { quote?: string } | undefined;
  return quoteData?.quote ?? null;
}

/**
 * Get the count of shorts created by a specific member
 */
export async function getMemberShortCount(
  groupId: string,
  memberUuid: string,
): Promise<number> {
  const shortsCollection = collection(firestore, `groups/${groupId}/shorts`);
  const q = query(shortsCollection, where("created_by", "==", memberUuid));
  const result = await getCountFromServer(q);
  return result.data().count;
}

/**
 * Get all member stats in parallel
 */
export async function getMemberStats(
  groupId: string,
  memberUuid: string,
): Promise<{
  quoteCount: number;
  photoCount: number;
  shortCount: number;
  eventCount: number;
  latestQuote: string | null;
}> {
  const [quoteCount, photoCount, shortCount, eventCount, latestQuote] =
    await Promise.all([
      getMemberQuoteCount(groupId, memberUuid),
      getMemberPhotoCount(groupId, memberUuid),
      getMemberShortCount(groupId, memberUuid),
      getMemberEventCount(groupId, memberUuid),
      getMemberLatestQuote(groupId, memberUuid),
    ]);

  return {
    quoteCount,
    photoCount,
    shortCount,
    eventCount,
    latestQuote,
  };
}
